/* eslint-disable react/no-multi-comp */
import { useSelector } from 'react-redux'
import { Box } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'

import type {
  Invoice as GraphqlInvoice,
  RefundInvoice,
} from '~/api/graphql/generated/types'
import Link from '~/components/common/link/Link'
import { getPatient } from '~/store/reducers/patients'
import type { Invoice } from '~/types'
import {
  type UIRoliSection,
  useGetInvoiceSectionsWithLimit,
} from '~/utils/finance'

import type { InvoiceLineItemProps } from '../dashboard/clients/balance/table/invoices/InvoiceTableEventTypeCell'
import { MultipleLinesCell } from '../dashboard/clients/balance/table/invoices/MultipleLinesCell'

const useStyles = makeStyles(
  (theme) => ({
    link: {
      textDecorationColor: theme.colors.secondaryText,
      color: theme.colors.secondaryText,
      fontSize: '1.4rem',
    },
  }),
  { name: 'InvoiceTablePatientNameCell' },
)

interface InvoiceTablePatientNameCellProps {
  hideRetailOrder?: boolean
  invoice: Invoice | GraphqlInvoice | RefundInvoice
  multipleRowsLimit?: number
  setInitialItemQueryParam?: (tableRowIndex?: number) => void
  tableRowIndex?: number
}

const InvoiceLineItem = ({
  tableRowIndex,
  setInitialItemQueryParam,
  ...section
}: InvoiceLineItemProps) => {
  const classes = useStyles()

  const patientId = 'patient' in section ? section.patient?.id : ''
  const patientName = 'patient' in section ? section.patient?.name : ''

  return (
    <Link
      className={classes.link}
      id="credit-adjustment-patient-link"
      target="_self"
      to={`/patient/${patientId}`}
      onClick={() => setInitialItemQueryParam?.(tableRowIndex)}
    >
      {patientName}
    </Link>
  )
}

type RetailOrderLineItemProps = UIRoliSection & {
  setInitialItemQueryParam?: (index?: number) => void
  tableRowIndex?: number
}

const RetailOrderLineItem = ({
  tableRowIndex,
  setInitialItemQueryParam,
  ...section
}: RetailOrderLineItemProps) => {
  const classes = useStyles()
  const patients = section.patients ?? []

  return (
    <Box display="block" overflow="hidden" textOverflow="ellipsis">
      {patients.map((patient, index: number) => (
        <Link
          className={classes.link}
          id="credit-adjustment-patient-link"
          key={patient.id}
          style={{ textDecoration: 'none' }}
          target="_self"
          to={`/patient/${patient.id}`}
          onClick={() => setInitialItemQueryParam?.(tableRowIndex)}
        >
          <>
            <span style={{ textDecoration: 'underline' }}>{patient.name}</span>
            <span style={{ textDecoration: 'none' }}>
              {index < patients.length - 1 && ',\u00A0'}
            </span>
          </>
        </Link>
      ))}
    </Box>
  )
}

export const InvoiceTablePatientNameCell = ({
  hideRetailOrder = false,
  invoice,
  multipleRowsLimit,
  tableRowIndex,
  setInitialItemQueryParam,
}: InvoiceTablePatientNameCellProps) => {
  const classes = useStyles()

  const sections = useGetInvoiceSectionsWithLimit(invoice, multipleRowsLimit)

  const invoicePatientId = sections ? null : (invoice as Invoice).patientId
  const patientFromStore = useSelector(getPatient(invoicePatientId))

  if (sections && !R.isEmpty(sections)) {
    return (
      <MultipleLinesCell
        InvoiceLineItem={InvoiceLineItem}
        RetailOrderLineItem={RetailOrderLineItem}
        hideRetailOrder={hideRetailOrder}
        sections={sections}
        setInitialItemQueryParam={setInitialItemQueryParam}
        tableRowIndex={tableRowIndex}
      />
    )
  }

  if (!patientFromStore) {
    return null
  }

  return (
    <Link
      className={classes.link}
      id="credit-adjustment-patient-link"
      target="_blank"
      to={`/patient/${patientFromStore.id}`}
      onClick={() => setInitialItemQueryParam?.(tableRowIndex)}
    >
      {patientFromStore.name}
    </Link>
  )
}
